/**
 * Tenant config — populated by `scripts/build-tenant.js` per build.
 *
 * The actual JSON file (`src/tenant.config.json`) is gitignored and
 * regenerated for every tenant build. A default copy is committed
 * locally for `npm run dev`.
 */
import config from "@/tenant.config.json";

export interface TenantConfig {
	brand: string;
	fullName: string;
	tagline: string;
	copyright: string;
	logo: string;
	/** Optional alternate logo for dark theme (useful when the wordmark
	 *  has dark text that becomes unreadable on a dark canvas). */
	logoDark?: string;
	themeColor: string;
	cardDesign?: string;
	/** When true, the logo asset already contains the brand name —
	 *  components should NOT render an extra `{brand}` text next to it. */
	logoIncludesText?: boolean;
	/** Path to the card background image (relative to /). If empty/missing,
	 *  the card falls back to a CSS gradient. */
	cardBackground?: string;
	/** Per-card-type background images, keyed by the backend `internalCode`
	 *  (K001/E001/E002 — see backend card_types.py). Codes without an entry
	 *  fall back to `cardBackground`. */
	cardBackgrounds?: Record<string, string>;
	/** Whether to print the card product name (from `cardProduct.name`) on the
	 *  card face. Defaults to true; set false to omit it (e.g. when the artwork
	 *  already carries the product tier). */
	showCardProductName?: boolean;
	/** Whether to expose the light/dark theme toggle in the UI. Defaults to true.
	 *  When false, the app is locked to `defaultTheme` and the toggle button is hidden. */
	themeToggle?: boolean;
	/** Theme used on first visit, and the locked theme when `themeToggle` is false.
	 *  Defaults to "dark". */
	defaultTheme?: "light" | "dark";
	/** The tenant logo has white lettering and needs dark surfaces: the
	 *  desktop navbar renders as a dark pill, and light contexts (e.g. the
	 *  mobile auth header) put the logo on a compact dark plate. */
	darkNav?: boolean;
	/** Telegram support link (e.g. "https://t.me/exwire_support"). When set, a
	 *  "Support" button appears in the header; when absent, no button is shown. */
	telegramSupport?: string;
}

// JSON imports widen literal unions (e.g. defaultTheme) to `string` — cast through.
export const tenant: TenantConfig = config as TenantConfig;

/**
 * Card background image for a given card type. Looks up `cardBackgrounds` by
 * the backend `internalCode` (K001/E001/E002); null/unknown codes and codes
 * without an entry fall back to `cardBackground`.
 */
export function cardBackgroundFor(internalCode?: string | null): string | undefined {
	return (
		(internalCode ? tenant.cardBackgrounds?.[internalCode] : undefined) ??
		tenant.cardBackground
	);
}
